import React from 'react';
import uuidv4 from 'uuidv4';
import extend from 'lodash/extend';
import { useQuery } from 'react-apollo';
import { Storage } from 'aws-amplify';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Divider from '@material-ui/core/Divider';
import Form from 'components/form';
import Input from 'components/form/Input';
import PhoneInput from 'components/form/PhoneInput';
import Radio from 'components/form/Radio';
import Select from 'components/form/Select';
import DropZone from 'components/form/Dropzone';
import Button from 'components/common/Button';
import Spin from 'components/common/Spin';
import STATES from 'constant/states';
import TIMEZONE from 'constant/timezones';
import PERSONALITY_TYPE from 'constant/personality-types';
import GENDER from 'constant/gender-pronounce';
import { useSelector, useDispatch } from 'react-redux';
import { useMutation } from '@apollo/react-hooks';
import useSnack from 'hooks/useSnack';
import { GET_PROFILE, UPDATE_PROFILE } from 'graphql/hc/profile';
import { UPDATE_USER } from 'graphql/Home';
import { updateUser } from 'reducers/user';
import useStyles from '../styles';
import { validatePhone } from '../../../../utils';
import CheckboxGroup from '../../../../components/form/CheckboxGroup';

const BUCKET = process.env.REACT_APP_BUCKET;
const FormItem = Form.FormItem;

const Quiz = ({
  user,
  personal_info,
  form: { getFieldDecorator, validateFields, setFieldsValue },
}) => {
  const classes = useStyles();
  const [update] = useMutation(UPDATE_PROFILE);
  const snack = useSnack();

  const onUpdate = () => {
    return validateFields().then((values) => {
      values.personality_type = values.personality_type?.value;
      return update({
        variables: {
          id: user.id,
          input: {
            personal_info: extend(personal_info, values),
          },
        },
      }).then(
        () => {
          snack.success('Profile saved');
        },
        (e) => {
          snack.error(e.message);
        },
      );
    });
  };

  const onRemoveFile = (file) => {
    Storage.remove(file.url || file.name);
    setFieldsValue({
      personality_result_url: null,
    });
  };

  const handleFileSelect = (files, onProgress, onSuccess, onError) => {
    const file = files[0];
    if (!file) {
      setFieldsValue({
        personality_result_url: null,
      });
    } else {
      const key = uuidv4() + '-' + file.name;

      Storage.put(key, file, {
        progressCallback: (progress) => {
          const pc = (progress.loaded / progress.total) * 100;
          if (onProgress) {
            onProgress({ percent: pc }, file);
          }
        },
      })
        .then((result) => {
          const url = `https://${BUCKET}.s3.us-east-2.amazonaws.com/public/${result.key}`;
          if (onSuccess) {
            onSuccess(url, file);
          }
          setFieldsValue({
            personality_result_url: url,
          });
        })
        .catch((err) => {
          if (onError) {
            onError(err, file);
          }
        });
    }
  };

  return (
    <Card className={classes.card}>
      <CardContent className={classes.cardContent}>
        <Grid container spacing={4}>
          <Grid item md={8}>
            <Typography gutterBottom variant="h5">
              Personality Quiz
            </Typography>
            <Typography variant="body2" style={{ marginBottom: 32 }}>
              Discover your personality type{' '}
              <a target="_blank" href="https://www.16personalities.com/">
                here
              </a>
            </Typography>
            <Form>
              <FormItem>
                {getFieldDecorator('personality_type', {
                  initialValue: PERSONALITY_TYPE.find(
                    (i) => i.value === personal_info?.personality_type,
                  ),
                })(
                  <Select
                    isMulti={false}
                    options={PERSONALITY_TYPE}
                    label="Personality Type Result"
                  />,
                )}
              </FormItem>
              <FormItem>
                {getFieldDecorator('personality_result_url', {
                  initialValue: personal_info?.personality_result_url,
                })(
                  <DropZone
                    attachments={
                      personal_info?.personality_result_url
                        ? [{ name: personal_info?.personality_result_url }]
                        : []
                    }
                    label="Upload a screenshot of your results here"
                    onUpload={handleFileSelect}
                    onRemove={onRemoveFile}
                    listType="card"
                  />,
                )}
              </FormItem>
            </Form>
          </Grid>
        </Grid>
      </CardContent>
      <Divider />
      <CardContent className={classes.footer}>
        <Button variant="contained" color="primary" onClick={onUpdate}>
          Save
        </Button>
      </CardContent>
    </Card>
  );
};

const QuizForm = Form.create()(Quiz);

const GeneralDetails = ({
  user,
  form: { getFieldDecorator, validateFields },
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const snack = useSnack();
  const currentUser = useSelector((s) => s.user.user);
  const { data, loading } = useQuery(GET_PROFILE, {
    variables: { id: user.id },
  });
  const [update] = useMutation(UPDATE_USER);
  const [updateProfile] = useMutation(UPDATE_PROFILE);

  const onSubmit = () => {
    return validateFields().then(async (values) => {
      values.state_origin = values.state_origin?.value;
      values.state_residence = values.state_residence?.value;
      const {
        user_first_name,
        user_last_name,
        user_preferred_name,
        gender_pronounce,
        timezone,
        bio,
        ...other
      } = values;
      const timezoneValue = timezone?.value;

      try {
        await updateProfile({
          variables: {
            id: user.id,
            input: {
              bio,
              personal_info: extend(profile, other),
            },
          },
        });

        await update({
          variables: {
            sub: user.sub,
            data: {
              user_first_name,
              user_last_name,
              gender_pronounce: gender_pronounce ? `{${gender_pronounce.join(', ')}}` : '',
              timezone: timezoneValue,
              state: values.state_residence,
              user_phone: values.user_phone,
              user_preferred_name,
            },
          },
        });
        if (currentUser.id === user.id) {
          dispatch(
            updateUser({
              fname: user_first_name,
              lname: user_last_name,
              gender_pronounce: gender_pronounce,
              timezone: timezoneValue,
              user_phone: values.user_phone,
              user_preferred_name,
            }),
          );
        }
        snack.success('Profile saved');
      } catch (e) {
        const { message } = e;
        if (message.includes('users_user_phone_key')) {
          snack.error('Phone number already in use. Try a different number.');
        } else {
          snack.error(message);
        }
      }
    });
  };

  if (!data) return <Spin spinning />;
  const profile = data.hc_profile[0].personal_info;
  const genders = Array.isArray(user?.gender_pronounce)
    ? user.gender_pronounce
    : user.gender_pronounce?.replace(/[{}]/g, '').split(', ');

  return (
    <React.Fragment>
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <Grid container spacing={4}>
            <Grid item md={8}>
              <Typography className={classes.header} variant="h5">
                Personal Info
              </Typography>
              <Form>
                <FormItem>
                  {getFieldDecorator('user_first_name', {
                    initialValue: user.fname,
                    rules: [{ required: true }],
                  })(<Input fullWidth label="First Name" />)}
                </FormItem>
                <FormItem>
                  {getFieldDecorator('user_last_name', {
                    initialValue: user.lname,
                    rules: [{ required: true }],
                  })(<Input fullWidth label="Last Name" />)}
                </FormItem>
                <FormItem>
                  {getFieldDecorator('user_preferred_name', {
                    initialValue: user.user_preferred_name,
                    rules: [{ required: false }],
                  })(<Input fullWidth label="Preferred Name" />)}
                </FormItem>
                <FormItem>
                  {getFieldDecorator('bio', {
                    initialValue: data.hc_profile[0].bio,
                    rules: [{ required: true }],
                  })(
                    <Input
                      multiline
                      rows="4"
                      fullWidth
                      label="About yourself"
                      placeholder="Tell us about yourself"
                    />,
                  )}
                </FormItem>
                <FormItem>
                  {getFieldDecorator('personal_email', {
                    initialValue: profile.personal_email,
                    rules: [{ required: true, type: 'email' }],
                  })(
                    <Input
                      fullWidth
                      label="Personal email address"
                      hint="We will pay you through PayPal using this email"
                    />,
                  )}
                </FormItem>
                <FormItem>
                  {getFieldDecorator('user_phone', {
                    initialValue: user.user_phone,
                    rules: [{ required: true }, { validator: validatePhone }],
                  })(
                    <PhoneInput
                      fullWidth
                      label="Cell Phone"
                      placeholder="Enter cell phone number"
                    />,
                  )}
                </FormItem>
                <FormItem>
                  {getFieldDecorator('gender_pronounce', {
                    initialValue: genders,
                  })(
                    <CheckboxGroup
                      options={GENDER}
                      label="Preferred Gender Pronouns"
                    />,
                  )}
                </FormItem>
                <FormItem>
                  {getFieldDecorator('state_origin', {
                    initialValue: STATES.find(
                      (s) => s.value === profile.state_origin,
                    ),
                  })(
                    <Select
                      isMulti={false}
                      options={STATES}
                      label="State of Origin"
                      description="Which state(s) are you from? Feel free to add aditional lines as necessary and list other countries if you have lived internationally"
                    />,
                  )}
                </FormItem>
                <FormItem>
                  {getFieldDecorator('state_residence', {
                    initialValue: STATES.find(
                      (s) => s.value === profile.state_residence,
                    ),
                  })(
                    <Select
                      isMulti={false}
                      options={STATES}
                      label="State of Residence"
                      description="Where are you currently living? Most likely this will be the state your college is located in. Don’t  worry if you aren’t officially registered there - we just wan to know for time zone and client matching purposes."
                    />,
                  )}
                </FormItem>
                <FormItem>
                  {getFieldDecorator('timezone', {
                    initialValue: TIMEZONE.find(
                      (s) => s.value === user.timezone,
                    ),
                  })(
                    <Select
                      isMulti={false}
                      options={TIMEZONE}
                      label="Time zone"
                      description="Your primary time zone"
                    />,
                  )}
                </FormItem>
              </Form>
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <CardContent className={classes.footer}>
          <Button variant="contained" color="primary" onClick={onSubmit}>
            Save
          </Button>
        </CardContent>
      </Card>
      <QuizForm user={user} personal_info={profile} />
    </React.Fragment>
  );
};

export default Form.create()(GeneralDetails);
